import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { VEYRO, COLORS } from "@/lib/veyro";
import { fetchVeyroProduct, fetchShippingProtection } from "@/lib/shopify";
import { trackPixelEvent } from "@/lib/metaPixel";

const CartContext = createContext(null);
const round2 = (n) => Math.round(n * 100) / 100;
const FREE_SHIP_THRESHOLD = 100;

export const CartProvider = ({ children }) => {
  const [qty, setQtyState] = useState(() => {
    try {
      const q = parseInt(localStorage.getItem("veyro_qty") || "0", 10);
      return isNaN(q) ? 0 : Math.max(0, Math.min(q, 10));
    } catch (e) {
      return 0;
    }
  });
  const [protection, setProtection] = useState(() => {
    try {
      return localStorage.getItem("veyro_protect") === "1";
    } catch (e) {
      return false;
    }
  });
  const [color, setColorState] = useState(() => {
    try {
      const c = localStorage.getItem("veyro_color");
      return COLORS.some((k) => k.name === c) ? c : COLORS[0].name;
    } catch (e) {
      return COLORS[0].name;
    }
  });
  const [isOpen, setIsOpen] = useState(false);
  const [shopifyProduct, setShopifyProduct] = useState(null);
  const [protectionProduct, setProtectionProduct] = useState(null);

  useEffect(() => {
    try {
      localStorage.setItem("veyro_qty", String(qty));
      localStorage.setItem("veyro_protect", protection ? "1" : "0");
      localStorage.setItem("veyro_color", color);
    } catch (e) {}
  }, [qty, protection, color]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, sp] = await Promise.all([fetchVeyroProduct(), fetchShippingProtection()]);
        if (cancelled) return;
        if (p) setShopifyProduct(p);
        if (sp) setProtectionProduct(sp);
      } catch (e) {}
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedVariant = useMemo(() => {
    const variants = shopifyProduct?.variants || [];
    if (variants.length === 0) return null;
    const syns = COLORS.find((c) => c.name === color)?.match || [color.toLowerCase()];
    return (
      variants.find((v) => {
        const t = `${v.title} ${v.options}`.toLowerCase();
        return syns.some((s) => t.includes(s));
      }) || variants[0]
    );
  }, [shopifyProduct, color]);

  const variantId = selectedVariant?.id || shopifyProduct?.variantId || null;
  const price = selectedVariant?.price > 0 ? selectedVariant.price : shopifyProduct?.price > 0 ? shopifyProduct.price : VEYRO.price;
  const compareAt = selectedVariant?.compareAt ?? shopifyProduct?.compareAt ?? VEYRO.compareAt;
  const setColor = useCallback((c) => setColorState(c), []);

  const addToCart = useCallback(
    (n = 1) => {
      setQtyState((q) => Math.min(q + n, 10));
      setIsOpen(true);
      trackPixelEvent("AddToCart", {
        content_name: VEYRO.name,
        content_type: "product",
        contents: [{ id: VEYRO.pixelId, quantity: n }],
        value: round2(n * price),
        currency: "USD",
      });
    },
    [price]
  );

  const setQty = useCallback((n) => setQtyState(Math.max(0, Math.min(n, 10))), []);

  const toggleProtection = useCallback((on) => setProtection(on), []);

  const pricing = useMemo(() => {
    const subtotal = round2(qty * price);
    const compareTotal = compareAt ? round2(qty * compareAt) : subtotal;
    const savings = round2(Math.max(compareTotal - subtotal, 0));
    const protActive = Boolean(protection && protectionProduct && qty > 0);
    const protTotal = protActive ? round2(protectionProduct.price) : 0;
    const total = round2(subtotal + protTotal);
    const freeShipProgress = Math.min(subtotal / FREE_SHIP_THRESHOLD, 1);
    return {
      qty,
      price,
      compareAt,
      subtotal,
      savings,
      protection: protActive ? protectionProduct : null,
      total,
      count: qty + (protActive ? 1 : 0),
      freeShipProgress,
      freeShipUnlocked: subtotal >= FREE_SHIP_THRESHOLD,
    };
  }, [qty, price, compareAt, protection, protectionProduct]);

  const value = {
    qty,
    isOpen,
    openCart: () => setIsOpen(true),
    closeCart: () => setIsOpen(false),
    addToCart,
    setQty,
    protection,
    toggleProtection,
    protectionProduct,
    pricing,
    shopifyProduct,
    color,
    setColor,
    variantId,
    price,
    compareAt,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
